import { customElement, FASTElement, observable } from '@microsoft/fast-element';
import { Route } from '@microsoft/fast-router';
import { SellerDashboardTemplate as template } from './seller-dashboard.template';
import { SellerDashboardStyles as styles } from './seller-dashboard.styles';
import { SellerTile } from '../../components/page-specific/seller-dashboard-components/seller-tile/seller-tile';
import { IVppa, Project } from '../../types';
import { MyProjectsContainer } from '../../components/page-specific/seller-dashboard-components/my-projects-container/my-projects';
import { ProjectModal } from '../../components/page-specific/project-modal/project-modal';
import { VppaModal } from '../../components/modals/vppa-modal/vppa-modal';
import { CancelModal } from '../../components/modals/cancel-modal/cancel-modal';
import { logger } from '@genesislcap/foundation-comms';
import { INegotiationService, IProjectModalService, IVppaService } from '../../services';
import { IStructuredChatService } from '../../services/structured-chat.service';
import { notifyError } from '../../utils/notify';
import { NEGOTIATION_STATUS } from '../../utils';
import { IExecutionService } from '../../services/execution.service';
import { IUserService } from '../../services/user.service';
import {IThirdPartyService} from "../../services/third-party.service";
import {MoreInfoModal} from "../../components/common/more-info/more-info-modal/more-info-modal";

const name = 'dashboard-route';

@customElement({
  name,
  template,
  styles,
})
export class SellerDashboard extends FASTElement {
  @IUserService userService: IUserService;
  @IProjectModalService projectModalService: IProjectModalService;
  @IStructuredChatService structuredChatService: IStructuredChatService;
  @INegotiationService negotiationService: INegotiationService;
  @IExecutionService executionService: IExecutionService;
  @IThirdPartyService thirdPartyService: IThirdPartyService;

  @observable hasProject: Boolean = false;
  @observable selectedProject: Project;
  @observable projectModal: ProjectModal;
  @observable showVppaModal: boolean = false;
  @observable showCancelModal: boolean = false;
  @observable projectsList: Array<Project> = [];
  @observable selectedVppa: IVppa;
  @observable arteriaUrl: String;

  public tileElement: SellerTile;
  public myProjectsElement: MyProjectsContainer;
  public vppaModal: VppaModal;
  public cancelModal: CancelModal;
  public moreInfoModal: MoreInfoModal;


  @IVppaService vppaService: IVppaService;
  @observable projectsListEmpty: boolean = false;
  constructor() {
    super();
    this.thirdPartyService.loadArteriaUrl();
  }

  connectedCallback() {
    super.connectedCallback();
  }

  public openModal() {
    this.projectModal.openModal();
  }

  public openVppaModal() {
    this.vppaModal.openModal(false, '', this.selectedProject.PROJECT_ID);
  }

  public changeSelectedProject(selectedProject: Project) {
    this.selectedProject = selectedProject;
  }

  public setProjectList(projectsList: Array<Project>) {
    this.projectsList = projectsList;
    this.tileElement.setProjectsList(this.projectsList);
  }

  public hasProjectSet(hasProject: Boolean) {
    this.hasProject = hasProject;
  }

  public createProject() {
    this.projectModalService.modalMode = 'create';
    this.openModal();
  }

  public openDraftProjectModal(selectedDraftProject: Project) {
    this.projectModalService.modalMode = 'create';
    this.openModal();
    this.projectModal.populateAllForms(selectedDraftProject);
  }

  public editProject() {
    this.projectModalService.modalMode = 'edit';
    this.openModal();
    this.projectModal.populateAllForms(this.selectedProject);
  }

  public editVppa(data) {
    this.vppaModal.openModal(true, data.VPPA_ID, null, data.TICKER);
  }

  public copyVppa(data) {
    // not an update but want to copy the data from the existing vppa
    this.vppaModal.openModal(false, data.VPPA_ID, null, data.TICKER);
  }

  public cancelVppa(data: IVppa) {
    this.vppaService.eventVppaCancel(data.VPPA_ID, data.CONTRACT_TYPE).then(
      (response) => {
        if (response && response.MESSAGE_TYPE === 'EVENT_ACK') {
          this.cancelModal.closeDialog();
        } else {
          notifyError(response.ERROR[0].TEXT);
          logger.error(response.ERROR);
        }
      },
      (error) => {
        notifyError(error);
        logger.error(error);
      }
    );
  }

  public deleteVppa(data) {
    this.cancelModal.openModal(
      data,
      'VPPA',
      'Are you sure you wish to cancel ' + data.TICKER + '?'
    );
  }

  public reviewProposal(data) {
    this.structuredChatService.openStructuredChat({
      selectedVPPAIDs: [],
      selectedRFQIDs: [],
      selectedNegotiationID: data.NEGOTIATION_ID,
    });
  }

  public clearRejected(data) {
    this.negotiationService.eventNegotiationClearReject(data.NEGOTIATION_ID);
  }

  public reviewAndClearRejected(data) {
    this.reviewProposal(data);
    this.clearRejected(data);
  }

  public goToExecutionPage(data) {
    this.executionService.setNegotiationId(data.NEGOTIATION_ID);
    Route.path.push('execution');
  }

  public goToArteriaPage(data) {
    this.thirdPartyService.openArteriaNegotiations();
  }

  public goToArteriaNegotiationsPage(data) {
    if (data.NEGOTIATION_STATUS == NEGOTIATION_STATUS.EXECUTED) {
      this.thirdPartyService.openArteriaNegotiations()
      return;
    }
    //TODO select URL based on status and use transactionUUID instead of project?
    const projectUniqueIdentifier = data.PROJECT_UUID;
    if (projectUniqueIdentifier) {
      this.thirdPartyService.openArteriaChecklist(projectUniqueIdentifier);
    } else {
      this.thirdPartyService.openArteriaNegotiations();
    }
  }

  public getCurrentVppa(vppaId: string) {
    this.vppaService.getVppa(vppaId).then((result) => {
      this.selectedVppa = { ...result.REPLY[0] };
    });
  }

  public isProjectListEmpty(projectList) {
    if (!(projectList.length && projectList.length > 0)) {
      this.projectsListEmpty = true;
    } else {
      this.projectsListEmpty = false;
    }
  }

  public openMoreInfoModal(data) {
    this.moreInfoModal.openMoreInfoModal(data);
  }
}
